"use client";

import { useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Field,
  FieldLabel,
  FieldError,
  FieldGroup,
} from "@/components/ui/field";
import { GithubIcon, GoogleIcon } from "./icons";

interface EmailStepProps {
  form: UseFormReturn<{ email: string }>;
  isSubmitting: boolean;
  onSubmit: (data: { email: string }) => void;
  onSocialSignIn: (provider: "github" | "google") => void;
  ctaLabel?: string;
  showReferralCode?: boolean;
  referralCode?: string;
  referralCodeError?: string | null;
  onReferralCodeChange?: (value: string) => void;
}

export function EmailStep({
  form,
  isSubmitting,
  onSubmit,
  onSocialSignIn,
  ctaLabel,
  showReferralCode = false,
  referralCode = "",
  referralCodeError = null,
  onReferralCodeChange,
}: EmailStepProps) {
  const [referralOpen, setReferralOpen] = useState(referralCode !== "");

  return (
    <>
      <div className="space-y-2">
        {/* <Button
          variant="outline"
          className="w-full"
          disabled={isSubmitting}
          onClick={() => onSocialSignIn("github")}
        >
          <GithubIcon className="mr-2 h-4 w-4" />
          Continuar con GitHub
        </Button> */}
        <Button
          variant="outline"
          className="w-full"
          disabled={isSubmitting}
          onClick={() => onSocialSignIn("google")}
        >
          <GoogleIcon className="mr-2 h-4 w-4" />
          Continuar con Google
        </Button>
      </div>

      <div className="my-4 flex items-center gap-3">
        <span className="flex-1 border-t" aria-hidden="true" />
        <span className="text-muted-foreground text-xs uppercase">
          o continúa con email
        </span>
        <span className="flex-1 border-t" aria-hidden="true" />
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FieldGroup>
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  {...field}
                  id="email"
                  type="email"
                  placeholder="email@email.com"
                  aria-invalid={fieldState.invalid}
                  disabled={isSubmitting}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          {showReferralCode && !referralOpen && (
            <button
              type="button"
              onClick={() => setReferralOpen(true)}
              className="text-muted-foreground hover:text-foreground self-start text-xs underline underline-offset-2 transition-colors"
            >
              ¿Tienes un código de acceso o de referido?
            </button>
          )}
          {showReferralCode && referralOpen && (
            <Field data-invalid={!!referralCodeError}>
              <FieldLabel
                htmlFor="referral-code"
                className="text-muted-foreground text-xs"
              >
                Código de acceso o de referido (opcional)
              </FieldLabel>
              <Input
                id="referral-code"
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Tu código"
                value={referralCode}
                onChange={(event) => onReferralCodeChange?.(event.target.value)}
                disabled={isSubmitting}
                aria-invalid={!!referralCodeError}
                className="h-8 text-sm"
              />
              {referralCodeError && (
                <FieldError>{referralCodeError}</FieldError>
              )}
            </Field>
          )}
        </FieldGroup>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Enviando..." : (ctaLabel ?? "Continuar con Email")}
        </Button>
      </form>
    </>
  );
}
